import { createSelector } from '@reduxjs/toolkit';

import { CalculatedLine } from '@paragon/contract-library';
import { Line2D } from '@paragon/geometry-library';
import {
  findAndCalculateLine,
  getAllRelativeGeometries,
  getParentGuids,
  isExplicitCalculatedLine,
} from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { getAllDependentLinesOfCalculatedLine } from '../../library/relativeGeometry/getAllDependentLinesOfCalculatedLines';
import { RootState } from '..';
import { isInsertingMember, isInsertingReferenceLine } from '../slices/applicationSlice';
import { getCurrentRenderMode } from '../slices/camera/selectors';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getReferenceLineDisplayMode } from './getReferenceLineDisplayMode';
import { getReferenceLinesForActiveComponent } from './getReferenceLinesForActiveComponent';
import { getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';
import { getPickingObjectInfo } from './pickingSelectors';

export const getVisibleReferenceLineGuids = createSelector(
  [
    getCurrentRenderMode,
    getActiveComponentDesign,
    getReferenceLinesForActiveComponent,
    getSingleSelectedItemEntryOrNull,
    (state: RootState) => state.application.insertingObjectInfo,
    getPickingObjectInfo,
    (state: RootState) => state.application.inMemberCuttingMode,
    (state: RootState) => state.viewport.highlightedReferenceLineGuids,
    getReferenceLineDisplayMode,
  ],
  (
    renderMode,
    activeComponentDesign,
    referenceLines,
    selectedEntry,
    insertingObjectInfo,
    pickingObjectInfo,
    inMemberCuttingMode,
    highlightedLineSet,
    referenceLineDisplayMode,
  ): ReadonlySet<string> | null => {
    const component = activeComponentDesign?.component;
    if (renderMode !== 'design' || !component || referenceLines == null) return null;

    const geometries = getAllRelativeGeometries(component);
    const highlightedLineGuids = Object.keys(highlightedLineSet);

    let visibleReferenceLineGuids: Set<string>;

    const selectedMemberDefinitionLineGuid =
      selectedEntry?.type === 'member' && selectedEntry.item.relativeGeometry?.lineGuid;

    if (selectedMemberDefinitionLineGuid && referenceLineDisplayMode.type === 'none') {
      const selectedMemberDefinitionLine = referenceLines.find(
        ({ guid }) => guid === selectedMemberDefinitionLineGuid,
      )!;

      const parentLineGuids = isExplicitCalculatedLine(selectedMemberDefinitionLine)
        ? getParentGuids(selectedMemberDefinitionLine).filter(notNullish)
        : [];

      visibleReferenceLineGuids = new Set<string>([
        // the selected member must obviously be visible
        selectedMemberDefinitionLineGuid,

        ...highlightedLineGuids,
        ...parentLineGuids,
      ]);
    } else {
      switch (referenceLineDisplayMode.type) {
        case 'none':
          return null;

        case 'selecting':
          visibleReferenceLineGuids = new Set([
            ...highlightedLineGuids,
            ...referenceLines.filter(isExplicitCalculatedLine).map((line) => line.guid),
          ]);
          break;

        case 'picking':
          visibleReferenceLineGuids = getDisplayedReferenceLinesWhileInReferenceLinePickingMode();
          break;
      }
    }

    return visibleReferenceLineGuids;

    function exceptLineAndDescendants(lines: readonly CalculatedLine[], lineToRemove: CalculatedLine) {
      if (referenceLines == null) return [];

      const descendantGuids = getAllDependentLinesOfCalculatedLine(lineToRemove, referenceLines, geometries);
      const filteredLineGuids = new Set(descendantGuids.map((line) => line.guid).concat([lineToRemove.guid]));

      return lines.filter((line) => !filteredLineGuids.has(line.guid));
    }

    function getDisplayedReferenceLinesWhileInReferenceLinePickingMode(): Set<string> {
      const SHOW_NONE = new Set<string>();

      if (!referenceLines) return SHOW_NONE;

      // It's possible for the result of getSelectedItem to be null even if selectedItemType isn't, because the item might
      //   temporarily not exist (like during insertion cancellation).
      if (selectedEntry == null) return SHOW_NONE;

      const SHOW_ALL = new Set(referenceLines.map((line) => line.guid));

      // No filtering of lines should happen when choosing a member cut, since it's okay for a cut on a member to directly
      //   or indirectly reference one of its member lines or definition lines.
      if (
        inMemberCuttingMode ||
        (pickingObjectInfo?.type === 'pickingReferenceLine' && pickingObjectInfo.targetInfo.type === 'memberCut')
      ) {
        return SHOW_ALL;
      }

      if (selectedEntry?.type === 'referenceLine')
        return new Set(exceptLineAndDescendants(referenceLines, selectedEntry.item).map((line) => line.guid));

      // It's possible to insert a line while another object is selected, in which case the new line has precedence.
      if (isInsertingReferenceLine(insertingObjectInfo)) return SHOW_ALL;

      // When a member or bearing is being edited, we want to filter out any reference lines that would create a circular
      //   dependency (i.e., any lines that are dependent on the selected item's definition line).
      let referenceLineDependentItemBeingEdited = null;

      // The above case applies when a member or bearing is selected (and thus can be edited via the properties panel)...
      if (selectedEntry?.type === 'member' || selectedEntry?.type === 'bearing') {
        referenceLineDependentItemBeingEdited = selectedEntry.item;
      }

      if (referenceLineDependentItemBeingEdited) {
        const { relativeGeometry } = referenceLineDependentItemBeingEdited;
        if (!relativeGeometry) return SHOW_ALL;

        // A member or bearing can have reference line fields which can themselves be edited while the member or bearing
        // is selected. We must filter out these lines and their descendants as well.
        const guidOfLineUnderEdit =
          pickingObjectInfo?.type === 'pickingReferenceLine' && pickingObjectInfo.targetInfo.type === 'referenceLine'
            ? pickingObjectInfo.targetInfo.rootGuid
            : pickingObjectInfo?.type === 'pickingReferenceLineIntersection'
              ? pickingObjectInfo.referenceLineInfo.rootGuid
              : null;

        let linesToDisplay = referenceLines
          .filter(
            (line) =>
              line.guid === guidOfLineUnderEdit ||
              (line.lineType === 'Member' && line.memberGeometryGuid === relativeGeometry.guid),
          )
          .reduce(exceptLineAndDescendants, referenceLines)
          .map((line) => line.guid);
        const selectedBearing = selectedEntry?.type === 'bearing' ? selectedEntry.item : null;
        const selectedBearingRelativeGeometry = selectedBearing?.relativeGeometry;
        const pickingObjectInfoTargetInfo =
          pickingObjectInfo?.type === 'pickingReferenceLine' && pickingObjectInfo.targetInfo.type === 'bearingLine'
            ? pickingObjectInfo.targetInfo
            : null;
        if (
          selectedBearing &&
          selectedBearingRelativeGeometry &&
          selectedBearingRelativeGeometry.bearingGeometryType === 'Bearing' &&
          pickingObjectInfoTargetInfo &&
          pickingObjectInfoTargetInfo.bearingGeometryType === 'Bearing' &&
          component
        ) {
          linesToDisplay = linesToDisplay.filter((lineGuid) => {
            const line2D = findAndCalculateLine(lineGuid, component);
            if (!line2D) return true;
            let otherBearingLine: Line2D | null = null;
            const { propertyName } = pickingObjectInfoTargetInfo;
            switch (propertyName) {
              case 'bearingLineGuid':
                otherBearingLine = findAndCalculateLine(selectedBearingRelativeGeometry.lineGuid, component);
                break;

              case 'lineGuid':
                otherBearingLine = findAndCalculateLine(selectedBearingRelativeGeometry.bearingLineGuid, component);
                break;

              default:
                assertExhaustiveSwitch(propertyName);
            }
            if (!otherBearingLine) return true;
            return !line2D.isParallelTo(otherBearingLine);
          });
        }
        return new Set(linesToDisplay);
      }

      if (isInsertingMember(insertingObjectInfo)) return SHOW_ALL;

      return SHOW_NONE;
    }
  },
);
